import { KeyboardArrowRight } from '@mui/icons-material';
import {
  Icon,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { FinsightEntityField } from 'shared';
import { formatName } from 'utils/fields';

export function EnumRenderer({
  field,
  value,
  editable,
  onChange,
  variant,
}: {
  field: FinsightEntityField;
  value: unknown;
  editable?: boolean;
  onChange?: (value: unknown) => Promise<void>;
  variant?: 'menu';
}) {
  const [enumMenu, setEnumMenu] = useState<HTMLElement | null>(null);
  if (typeof value !== 'number' || !Array.isArray(field.schema.properties))
    return null;

  if (variant === 'menu') {
    return (
      <>
        <Typography
          textTransform="uppercase"
          color="text.secondary"
          fontWeight={300}
          letterSpacing={1}
          fontSize="0.6em"
          px={1}
        >
          {formatName(field.name ?? '')}
        </Typography>
        <MenuItem onClick={(e) => setEnumMenu(e.currentTarget)}>
          {field.schema.properties[value].ui?.adornments?.start?.icon && (
            <ListItemIcon>
              <Icon
                sx={{
                  color:
                    field.schema.properties[value].ui?.adornments?.start?.color,
                  fontSize: 26,
                }}
              >
                {field.schema.properties[value].ui?.adornments?.start?.icon}
              </Icon>
            </ListItemIcon>
          )}
          <ListItemText>
            {formatName(field.schema.properties[value].name ?? '')}
          </ListItemText>
          <ListItemSecondaryAction
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <KeyboardArrowRight />
          </ListItemSecondaryAction>
        </MenuItem>
        {editable && (
          <Menu
            anchorEl={enumMenu}
            open={Boolean(enumMenu)}
            onClose={() => setEnumMenu(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {field.schema.properties
              .map((x, i) => ({ ...x, index: i }))
              .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
              .map((prop) => (
                <MenuItem
                  disabled={prop.index === value}
                  key={prop.index}
                  value={prop.index}
                  onClick={() => {
                    onChange?.(prop.index);
                    setEnumMenu(null);
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    {prop.ui?.adornments?.start?.icon && (
                      <Icon
                        sx={{
                          color: prop.ui?.adornments?.start?.color,
                          fontSize: 26,
                        }}
                      >
                        {prop.ui?.adornments?.start?.icon}
                      </Icon>
                    )}
                    {formatName(prop.name ?? '')}
                  </Stack>
                </MenuItem>
              ))}
          </Menu>
        )}
      </>
    );
  }

  if (editable) {
    return (
      <TextField
        select
        label={formatName(field.name ?? '')}
        placeholder={
          field.schema.default
            ? field.schema.properties[Number(field.schema.default)].name
            : undefined
        }
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {field.schema.properties
          .map((x, i) => ({ ...x, index: i }))
          .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
          .map((prop) => (
            <MenuItem key={prop.index} value={prop.index}>
              <Stack direction="row" gap={1} alignItems="center">
                {prop.ui?.adornments?.start?.icon && (
                  <Icon
                    sx={{
                      color: prop.ui?.adornments?.start?.color,
                      fontSize: 26,
                    }}
                  >
                    {prop.ui?.adornments?.start?.icon}
                  </Icon>
                )}
                {formatName(prop.name ?? '')}
              </Stack>
            </MenuItem>
          ))}
      </TextField>
    );
  }

  return (
    <Stack direction="row" gap={2} alignItems="center">
      {field.schema.properties[value].ui?.adornments?.start?.icon && (
        <Icon
          sx={{
            color: field.schema.properties[value].ui?.adornments?.start?.color,
            fontSize: 26,
          }}
        >
          {field.schema.properties[value].ui?.adornments?.start?.icon}
        </Icon>
      )}
      <Typography>
        {formatName(field.schema.properties[value].name ?? '')}
      </Typography>
    </Stack>
  );
}
